/**
 * WlTenantFilter
 * ----------------------------------------------------------------------------
 * Filtro server-side de tenants para o painel White Label.
 *
 * - Popover + Command (estilo CrmSellerFilter).
 * - Mostra os 10 primeiros tenants por padrão.
 * - Busca server-side (RPC `wl_list_tenants`) com debounce de 300ms.
 * - Pesquisa cobre: nome do tenant, slug, nome do owner, email do owner e
 *   telefone do owner (sem cap de 1000 — tudo via RPC paginada).
 *
 * Props:
 *   - value:    tenant_id selecionado ou null (Todos)
 *   - onChange: callback (null = todos)
 *   - className: classes adicionais p/ se encaixar na ListPageFiltersBar
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  useWlTenantSearch,
  type WlTenantSearchItem,
} from "@/hooks/wl/useWlTenantSearch";

interface WlTenantFilterProps {
  value: string | null;
  onChange: (tenantId: string | null) => void;
  className?: string;
  label?: string;
}

export function WlTenantFilter({
  value,
  onChange,
  className,
  label = "Tenants",
}: WlTenantFilterProps) {
  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  // Debounce 300ms — evita disparar RPC a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setDebounced(rawSearch), 300);
    return () => clearTimeout(t);
  }, [rawSearch]);

  const { data: tenants = [], isFetching } = useWlTenantSearch(
    debounced,
    open, // só consulta quando o popover está aberto
  );

  // Cache local do label do selecionado (para exibir nome sem refetch).
  // Inicializa quando o usuário escolhe um tenant pela lista.
  const [selectedCache, setSelectedCache] = useState<WlTenantSearchItem | null>(
    null,
  );

  // Se o `value` mudar externamente e não tivermos cache, tenta achar na lista.
  useEffect(() => {
    if (!value) {
      setSelectedCache(null);
      return;
    }
    if (selectedCache?.id === value) return;
    const found = tenants.find((t) => t.id === value);
    if (found) setSelectedCache(found);
  }, [value, tenants, selectedCache]);

  const triggerLabel = useMemo(() => {
    if (!value) return label;
    return selectedCache?.name ?? "Tenant selecionado";
  }, [value, selectedCache, label]);

  const handleSelect = (tenant: WlTenantSearchItem | null) => {
    if (tenant) {
      setSelectedCache(tenant);
      onChange(tenant.id);
    } else {
      setSelectedCache(null);
      onChange(null);
    }
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setRawSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-6 text-[11px] sm:h-8 sm:text-sm px-2 sm:px-3 gap-2 font-normal justify-between",
            className,
          )}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <Building2 className="h-3 w-3 opacity-70 shrink-0" />
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronDown className="h-2.5 w-2.5 sm:h-3 sm:w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        {/* shouldFilter=false: filtragem 100% server-side */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome, slug, email, telefone..."
            value={rawSearch}
            onValueChange={setRawSearch}
          />
          <CommandList>
            {isFetching && tenants.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                Buscando...
              </div>
            ) : (
              <CommandEmpty>Nenhum tenant encontrado.</CommandEmpty>
            )}

            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted",
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <Building2 className="h-4 w-4 mr-2 opacity-50" />
                <span>Todos Tenants</span>
              </CommandItem>
            </CommandGroup>

            {tenants.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup
                  heading={
                    debounced
                      ? `Resultados (${tenants.length})`
                      : "10 mais recentes"
                  }
                >
                  {tenants.map((t) => {
                    const isSel = value === t.id;
                    return (
                      <CommandItem
                        key={t.id}
                        value={t.id}
                        onSelect={() => handleSelect(t)}
                        className="cursor-pointer"
                      >
                        <div
                          className={cn(
                            "mr-2 h-4 w-4 rounded-full border flex items-center justify-center shrink-0",
                            isSel
                              ? "border-primary bg-primary"
                              : "border-muted",
                          )}
                        >
                          {isSel && (
                            <Check className="h-2.5 w-2.5 text-primary-foreground" />
                          )}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate text-sm">{t.name}</span>
                          <span className="text-[11px] text-muted-foreground truncate">
                            {[
                              t.slug ? `@${t.slug}` : null,
                              t.owner_name,
                              t.owner_email,
                              t.owner_phone_masked,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
